// SPEC-qam-backlog Session 11 — per-user popup (toast) suppression.
// SPEC-1 senior-prefs (2026-09-23) — the same row now carries the Senior/Admin
// behaviour switches (autoAdvance, showExpectedActual, showQueueFooter) and
// savedFilters; `useNotificationPrefs` returns the whole UserPrefs under ONE
// query key so every reader (settings page, senior panel, detail page, Phase 2
// list view) sees the same cached row.
//
// Shared gating hook used by qa-detail-v3, prescreen-detail-v3, and qa-list so
// the suppression logic lives in ONE place and the two near-duplicate detail
// pages can't drift.
//
// Usage at a call site:
//   const { toast, showToast, prefs } = useNotificationToasts();
//   showToast("autoAdvance", { title: "Verified", description: "Loading next…" });
//   toast({ title: "Verified", description: "No more deliveries waiting." }); // always-on
//
// `showToast(group, args)` fires the toast only if the user has that group
// enabled. A group whose pref key is null (autoAdvance, SPEC-1 D3) is always
// shown. Always-on toasts (errors, end-of-queue tails, re-extraction status,
// the early-risk alert) must keep calling `toast(...)` directly so they are
// never gated. Critically: the end-of-queue tails share a *title* with the
// snoozable auto-advance confirmations — they live in distinct code branches,
// so we gate only the snoozable branch's call and leave the queue-empty branch
// calling `toast(...)` directly. (See the Session 11 brief watch-out.)

import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  DEFAULT_USER_PREFS,
  GROUP_TO_PREF_KEY,
  type NotificationGroup,
  type UserPrefs,
} from "@shared/notification-preferences";

export const NOTIFICATION_PREFS_QUERY_KEY = ["/api/user/notification-preferences"] as const;

async function fetchNotificationPrefs(): Promise<UserPrefs> {
  const res = await fetch("/api/user/notification-preferences", {
    credentials: "include",
  });
  if (!res.ok) {
    // Fail open: if prefs can't be read, show everything and keep auto-advance
    // on (never silently swallow toasts or strand the page because of a
    // settings fetch hiccup).
    return { ...DEFAULT_USER_PREFS };
  }
  const body = await res.json();
  return { ...DEFAULT_USER_PREFS, ...body };
}

/** Reads the current user's preferences — the whole row (cached, fail-open). */
export function useNotificationPrefs() {
  return useQuery<UserPrefs>({
    queryKey: NOTIFICATION_PREFS_QUERY_KEY,
    queryFn: fetchNotificationPrefs,
    staleTime: 1000 * 60 * 5,
    retry: false,
    placeholderData: { ...DEFAULT_USER_PREFS },
  });
}

type ToastArgs = Parameters<ReturnType<typeof useToast>["toast"]>[0];

export function useNotificationToasts() {
  const { toast } = useToast();
  const { data, isPlaceholderData } = useNotificationPrefs();
  const prefs: UserPrefs = data ?? DEFAULT_USER_PREFS;
  // false while `prefs` are still the placeholder defaults (before the first
  // response); true once the server answered or the fetch settled fail-open.
  const prefsReady = !isPlaceholderData;

  const isGroupEnabled = useCallback(
    (group: NotificationGroup): boolean => {
      const key = GROUP_TO_PREF_KEY[group];
      return key === null ? true : prefs[key];
    },
    [prefs],
  );

  /** Fires the toast only if the user has the given group enabled. `toast` is
   *  module-stable and `prefs` changes rarely, so this identity is stable
   *  except when the user actually changes a preference — safe to list in a
   *  consumer's useCallback deps without churning socket subscriptions. */
  const showToast = useCallback(
    (group: NotificationGroup, args: ToastArgs) => {
      if (!isGroupEnabled(group)) return undefined;
      return toast(args);
    },
    [isGroupEnabled, toast],
  );

  return { toast, showToast, prefs, prefsReady, isGroupEnabled };
}
